import type { PostListItem } from '@/entities/post';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';

import { campaignDetailCreatorSchema } from '../campaign-schema';
import type { CampaignDetailCreator, CampaignDetailPost } from '../campaign-type';
import type {
  CampaignCreatorListResponse,
  CampaignPostListResponse,
} from '../generated/campaign-type';

export type CampaignCreatorListData = {
  items: CampaignDetailCreator[];
  pagination: CampaignCreatorListResponse['pagination'];
};

export type CampaignPostListData = {
  items: CampaignDetailPost[];
  pagination: CampaignPostListResponse['pagination'];
  last_scraped_at?: CampaignPostListResponse['last_scraped_at'];
};

function toDateISOString(timestamp: number) {
  const milliseconds = timestamp < 10_000_000_000 ? timestamp * 1_000 : timestamp;
  return new Date(milliseconds).toISOString();
}

function isSourcePlatform(platform: string): platform is SOURCE_PLATFORM_TYPE {
  return Object.values(SOURCE_PLATFORM_TYPE).some((value) => value === platform);
}

export function mapCampaignCreatorListResponseToCampaignCreatorList(
  response: CampaignCreatorListResponse,
): CampaignCreatorListData {
  return {
    ...response,
    items: response.items.map((creator) =>
      campaignDetailCreatorSchema.parse({
        id: creator.id,
        platform: creator.platform,
        username: creator.username,
        nickname: creator.nickname ?? creator.username,
        profile_image_url: creator.profile_image_url ?? undefined,
        email: creator.email ?? undefined,
        label: creator.label ?? undefined,
        post_count: creator.post_count,
      }),
    ),
  };
}

export function mapCampaignPostListResponseToCampaignPostList(
  response: CampaignPostListResponse,
): CampaignPostListData {
  return {
    ...response,
    items: response.items.map((item) => {
      if (!isSourcePlatform(item.post.source.platform)) {
        throw new Error(`Unsupported campaign post platform: ${item.post.source.platform}`);
      }

      const scrapedAt = toDateISOString(item.post.scraped_at);
      const post: PostListItem = {
        ...item.post,
        date: toDateISOString(item.post.date),
        scraped_at: scrapedAt,
        updated_at: item.post.updated_at ?? scrapedAt,
        source: {
          ...item.post.source,
          platform: item.post.source.platform,
        },
        extraction: {
          ...item.post.extraction,
          title: item.post.extraction.title ?? undefined,
          sponsored: item.post.extraction.sponsored ?? undefined,
          video: {
            ...item.post.extraction.video,
            poster_url: item.post.extraction.video.poster_url ?? '',
            url: item.post.extraction.video.url ?? undefined,
          },
        },
        board_count: item.post.board_count ?? 0,
      };

      return {
        campaign_post_id: item.campaign_post_id,
        label: item.label ?? undefined,
        post,
        is_new: item.is_new,
        source: item.source,
      };
    }),
  };
}
